import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShieldCheck, ShieldOff, KeyRound, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from '@/components/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ApiErrorDisplay } from '@/components/features/error';
import { useUser, useDisableUser, useEnableUser, useResetUserPassword, useResetUserMfa } from '@/hooks/use-users';
import { useMerchant } from '@/hooks/use-merchants';
import type { AdminUser } from '@/types/user';

/** Menschenlesbarer Rollen-Name (Marken-/Rollen-Labels — sprachidentisch) */
function roleName(role: string): string {
  const labels: Record<string, string> = {
    platform_admin: 'Platform Admin',
    platform_it: 'Platform IT',
    platform_ops: 'Platform Ops',
    merchant_product_manager: 'Product Manager',
    merchant_it: 'Merchant IT',
    merchant_viewer: 'Merchant Viewer',
  };
  return labels[role] ?? role;
}

export function UserDetailPage() {
  const { sub } = useParams<{ sub: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('users');

  const { data: user, isLoading, error, refetch } = useUser(sub);
  const disableMutation = useDisableUser();
  const enableMutation = useEnableUser();
  const resetPwMutation = useResetUserPassword();
  const resetMfaMutation = useResetUserMfa();

  // Merchant-Name aufloesen, falls vorhanden
  const { data: merchant } = useMerchant(user?.merchantId ?? undefined);

  // Badge-Variante + uebersetztes Label — gleiche Logik wie in der Listenseite.
  const statusBadge = (u: AdminUser) => {
    if (!u.enabled) {
      return <Badge variant="secondary">{t('status.disabled')}</Badge>;
    }
    switch (u.status) {
      case 'CONFIRMED':
        return <Badge variant="success">{t('status.active')}</Badge>;
      case 'FORCE_CHANGE_PASSWORD':
        return <Badge variant="warning">{t('status.passwordChange')}</Badge>;
      case 'RESET_REQUIRED':
        return <Badge variant="warning">{t('status.resetPending')}</Badge>;
      case 'UNCONFIRMED':
        return <Badge variant="secondary">{t('status.unconfirmed')}</Badge>;
      default:
        return <Badge variant="outline">{u.status}</Badge>;
    }
  };

  if (!sub) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">{t('detail.missingId')}</p>
        <Button variant="link" onClick={() => navigate('/users')}>
          {t('common.backToList')}
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return <ApiErrorDisplay error={error} onRetry={() => refetch()} />;
  }

  if (!user) {
    return (
      <div className="py-12 text-center">
        <p className="text-muted-foreground">{t('detail.notFound')}</p>
        <Button variant="link" onClick={() => navigate('/users')}>
          {t('common.backToList')}
        </Button>
      </div>
    );
  }

  const handleDisable = async () => {
    try {
      await disableMutation.mutateAsync(user.sub);
      toast.success(t('detail.toast.disabled'));
    } catch (err) {
      toast.error(String(err));
    }
  };

  const handleEnable = async () => {
    try {
      await enableMutation.mutateAsync(user.sub);
      toast.success(t('detail.toast.enabled'));
    } catch (err) {
      toast.error(String(err));
    }
  };

  // Issue #390: Ein eingeladener, nie aktivierter Nutzer hat kein Passwort zum Zuruecksetzen.
  // Fuer diesen Status verschickt die Aktion die Einladung erneut (RESEND), statt zu resetten.
  const isPendingInvite = user.status === 'FORCE_CHANGE_PASSWORD' || user.status === 'UNCONFIRMED';

  const handleResetPassword = async () => {
    try {
      await resetPwMutation.mutateAsync(user.sub);
      toast.success(
        isPendingInvite
          ? t('detail.toast.inviteResent')
          : t('detail.toast.passwordReset'),
      );
    } catch (err) {
      toast.error(String(err));
    }
  };

  // Spec 0045: MFA zuruecksetzen
  const handleResetMfa = async () => {
    try {
      await resetMfaMutation.mutateAsync(user.sub);
      toast.success(t('detail.toast.mfaReset'));
      refetch();
    } catch (err) {
      toast.error(String(err));
    }
  };

  const merchantDisplay = user.merchantId
    ? (merchant ? `${merchant.name} (${user.merchantId})` : user.merchantId)
    : '—';

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('detail.title')}</h1>
          <p className="mt-1 font-mono text-sm text-muted-foreground">{user.email}</p>
        </div>
        <div className="flex gap-2">
          {/* Aktivieren / Deaktivieren Toggle */}
          {user.enabled ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  size="sm"
                  loading={disableMutation.isPending}
                >
                  <ShieldOff className="mr-2 h-4 w-4" />
                  {t('detail.disable')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('detail.disableDialog.title')}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('detail.disableDialog.description', { email: user.email })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDisable}>
                    {t('detail.disable')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : (
            <Button
              variant="default"
              size="sm"
              onClick={handleEnable}
              loading={enableMutation.isPending}
            >
              <ShieldCheck className="mr-2 h-4 w-4" />
              {t('detail.enable')}
            </Button>
          )}

          {/* Passwort zuruecksetzen — bei nicht aktiviertem Nutzer: Einladung erneut senden (#390) */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                loading={resetPwMutation.isPending}
              >
                <KeyRound className="mr-2 h-4 w-4" />
                {isPendingInvite ? t('detail.resendInvite') : t('detail.resetPassword')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {isPendingInvite ? t('detail.resetDialog.titleResend') : t('detail.resetDialog.titleReset')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {isPendingInvite
                    ? t('detail.resetDialog.descriptionResend', { email: user.email })
                    : t('detail.resetDialog.descriptionReset', { email: user.email })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleResetPassword}>
                  {isPendingInvite ? t('detail.resetDialog.confirmResend') : t('detail.actions.reset')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Spec 0045: MFA zuruecksetzen */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                loading={resetMfaMutation.isPending}
              >
                <ShieldAlert className="mr-2 h-4 w-4" />
                {t('detail.resetMfa')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('detail.mfaDialog.title')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('detail.mfaDialog.description', { email: user.email })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleResetMfa}>
                  {t('detail.actions.reset')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <Button variant="outline" size="sm" onClick={() => navigate('/users')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>
        </div>
      </div>

      {/* Detail Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('detail.card.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('detail.card.email')}</dt>
              <dd className="mt-1 text-sm">{user.email}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('detail.card.role')}</dt>
              <dd className="mt-1 text-sm">{roleName(user.role)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('detail.card.merchant')}</dt>
              <dd className="mt-1 text-sm font-mono">{merchantDisplay}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('detail.card.status')}</dt>
              <dd className="mt-1">{statusBadge(user)}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('detail.card.cognitoSub')}</dt>
              <dd className="mt-1 text-sm font-mono text-muted-foreground">{user.sub}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">{t('detail.card.created')}</dt>
              <dd className="mt-1 text-sm">
                {new Date(user.createdAt).toLocaleString('de-DE')}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}
